import { ApiError } from '../api'
import { buildFrame } from '../frame'
import { navigate } from '../router'
import { authState, describeWebAuthnError, passkeysSupported, registerPasskey, signInWithPasskey } from '../session'
import { h, toast } from '../ui'

/**
 * Handles both halves of getting in: signing in with an existing passkey, and
 * the one-time setup that registers the first one.
 */
export function signInView(root: HTMLElement, options: { next: string }): () => void {
  const frame = buildFrame({ user: null, subbar: false })
  const panel = h('div', { class: 'notice' }, [h('p', {}, ['Checking…'])])
  frame.stage.append(panel)
  frame.statusLeft.textContent = 'not signed in'
  root.append(frame.element)
  document.title = 'sign in · pastabin'

  let disposed = false

  if (!passkeysSupported()) {
    panel.replaceChildren(
      h('h1', {}, ['This browser has no passkey support']),
      h('p', {}, ['pastabin signs you in with a passkey, which needs WebAuthn.']),
    )
    return () => {}
  }

  void authState()
    .then((state) => {
      if (disposed) return
      if (!state.schemaReady) {
        renderNotReady(state.bootstrapReady)
        return
      }
      if (state.registered) renderSignIn()
      else renderSetup(state.bootstrapReady)
    })
    .catch((error: unknown) => {
      if (disposed) return
      // Show what the server actually said: this is the screen you land on when
      // a deployment is half-finished, and "reload in a moment" is useless then.
      const detail = error instanceof ApiError ? error.message : 'Reload in a moment.'
      panel.replaceChildren(h('h1', {}, ['Could not reach the kitchen']), h('p', {}, [detail]))
    })

  /** Deployment isn't finished — say which part, rather than looking broken. */
  function renderNotReady(bootstrapReady: boolean): void {
    panel.replaceChildren(
      h('h1', {}, ['Setup is not finished']),
      h('p', {}, ['The database schema has not been applied to this deployment yet.']),
      h('ul', { class: 'checklist' }, [
        h('li', { class: 'bad' }, ['D1 schema — not applied. Run the migrations for this Worker.']),
        bootstrapReady
          ? h('li', { class: 'good' }, ['Setup code — present.'])
          : h('li', { class: 'bad' }, ['Setup code — PASSKEY_BOOTSTRAP_CODE is not set.']),
      ]),
      h('p', { class: 'muted' }, ['Reload once the migrations have run.']),
    )
  }

  function renderSignIn(): void {
    const button = h(
      'button',
      { class: 'control primary', type: 'button', onclick: () => void go() },
      ['Sign in with a passkey'],
    )
    const message = h('p', { class: 'error' })

    panel.replaceChildren(
      h('h1', {}, ['Sign in to keep pastas']),
      h('p', {}, [
        'Your passkey unlocks pastabin — 1Password, Touch ID, or whatever holds it. Reading a public pasta needs no account; creating one does.',
      ]),
      h('div', { class: 'actions' }, [button]),
      message,
    )

    button.focus()

    async function go(): Promise<void> {
      button.disabled = true
      message.textContent = ''
      try {
        await signInWithPasskey()
        if (disposed) return
        // replace: true always re-dispatches, so the router re-renders the
        // target route now that currentUser() resolves to an identity.
        navigate(options.next, { replace: true })
      } catch (error) {
        if (disposed) return
        message.textContent = describeWebAuthnError(error)
        button.disabled = false
      }
    }
  }

  function renderSetup(bootstrapReady: boolean): void {
    if (!bootstrapReady) {
      panel.replaceChildren(
        h('h1', {}, ['Setup is not finished']),
        h('p', {}, [
          'No passkey is registered and PASSKEY_BOOTSTRAP_CODE is not set on this deployment, so there is no way to register the first one.',
        ]),
        h('p', { class: 'muted' }, ['Set the secret, redeploy, then reload this page.']),
      )
      return
    }

    const codeInput = h('input', {
      class: 'title-input',
      type: 'password',
      placeholder: 'Setup code',
      autocomplete: 'off',
      'aria-label': 'Setup code',
    })
    const labelInput = h('input', {
      class: 'title-input',
      type: 'text',
      placeholder: 'Your name (shown in the header)',
      value: 'owner',
      maxlength: '60',
      'aria-label': 'Account name',
    })
    const nameInput = h('input', {
      class: 'title-input',
      type: 'text',
      placeholder: 'Where is this passkey stored?',
      value: '1Password',
      maxlength: '60',
      'aria-label': 'Passkey name',
    })
    const button = h(
      'button',
      { class: 'control primary', type: 'button', onclick: () => void go() },
      ['Create the first passkey'],
    )
    const message = h('p', { class: 'error' })

    panel.replaceChildren(
      h('h1', {}, ['Claim this pastabin']),
      h('p', {}, [
        'No passkey is registered yet. Enter the setup code from your Worker secrets to register the first one — after that the code stops working.',
      ]),
      h('div', { class: 'setup-form' }, [codeInput, labelInput, nameInput, button]),
      h('p', { class: 'muted' }, [
        'Pick 1Password in the browser prompt if you want it stored there rather than on this device.',
      ]),
      message,
    )

    codeInput.focus()

    for (const input of [codeInput, labelInput, nameInput]) {
      input.addEventListener('keydown', (event) => {
        if ((event as KeyboardEvent).key === 'Enter') void go()
      })
    }

    async function go(): Promise<void> {
      if (!codeInput.value.trim()) {
        message.textContent = 'The setup code is required.'
        codeInput.focus()
        return
      }
      button.disabled = true
      message.textContent = ''
      try {
        await registerPasskey({
          bootstrapCode: codeInput.value.trim(),
          label: labelInput.value.trim() || 'owner',
          name: nameInput.value.trim() || 'passkey',
        })
        if (disposed) return
        toast('Passkey registered')
        navigate('/', { replace: true })
      } catch (error) {
        if (disposed) return
        message.textContent = describeWebAuthnError(error)
        button.disabled = false
      }
    }
  }

  return () => {
    disposed = true
  }
}
